import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { endpoints } from '../api/endpoints';
import type { Dashboard, Session, UserProgress, UserProgressPoint } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorBox, formatDate, formatScore, Loading, StatCard, useAsync } from '../components/common';

export function lastNDays(points: UserProgressPoint[], days = 30, today = new Date()): UserProgressPoint[] {
  const byDate = new Map(points.map((p) => [p.date.slice(0, 10), p]));
  const out: UserProgressPoint[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push(byDate.get(key) ?? { date: key, sessions: 0, avg_score: null, time_spent_seconds: 0 });
  }
  return out;
}

async function loadDashboard(): Promise<{ dashboard: Dashboard; progress: UserProgress }> {
  const [dashboard, progress] = await Promise.all([
    endpoints.bff.dashboard().catch(() => ({}) as Dashboard),
    endpoints.analytics.userProgress({ days: 30 }).catch(() => ({ points: [] }) as UserProgress),
  ]);
  return { dashboard, progress: dashboard.progress?.points?.length ? dashboard.progress : progress };
}

export function ActivityCharts({ points }: { points: UserProgressPoint[] }) {
  const { t } = useTranslation();
  const data = lastNDays(points).map((p) => ({
    date: p.date.slice(5),
    sessions: p.sessions,
    score: p.avg_score,
    minutes: Math.round((p.time_spent_seconds ?? 0) / 60),
  }));
  return (
    <section aria-label={t('dashboard.activity')} className="charts">
      <h2>{t('dashboard.activity')}</h2>
      <div className="chart-grid">
        <figure className="card chart">
          <figcaption>{t('dashboard.sessionsPerDay')}</figcaption>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Bar dataKey="sessions" fill="#4f8cff" name={t('dashboard.totalSessions')} />
            </BarChart>
          </ResponsiveContainer>
        </figure>
        <figure className="card chart">
          <figcaption>{t('dashboard.scoreTrend')}</figcaption>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis domain={[0, 100]} fontSize={11} />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#35c18f" connectNulls name={t('dashboard.avgScore')} />
            </LineChart>
          </ResponsiveContainer>
        </figure>
        <figure className="card chart">
          <figcaption>{t('dashboard.timePerDay')}</figcaption>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Bar dataKey="minutes" fill="#f5a524" name={t('dashboard.timeSpent')} />
            </BarChart>
          </ResponsiveContainer>
        </figure>
      </div>
    </section>
  );
}

export function SessionsTable({ sessions }: { sessions: Session[] }) {
  const { t, i18n } = useTranslation();
  if (!sessions.length) return <p className="muted">{t('sessions.empty')}</p>;
  return (
    <table>
      <thead>
        <tr>
          <th scope="col">{t('sessions.started')}</th>
          <th scope="col">{t('sessions.scenario')}</th>
          <th scope="col">{t('sessions.score')}</th>
          <th scope="col">{t('common.open')}</th>
        </tr>
      </thead>
      <tbody>
        {sessions.map((s) => (
          <tr key={s.id}>
            <td>{formatDate(s.started_at, i18n.language)}</td>
            <td>{s.scenario_id ? s.scenario_id.slice(0, 8) : t('sessions.freeMode')}</td>
            <td>{formatScore(s.total_score)}</td>
            <td>
              <Link to={`/sessions/${s.id}`}>{t('sessions.details')}</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, error, loading, reload } = useAsyncDashboard();

  const progress = data?.progress;
  const minutes = Math.round((progress?.total_time_spent_seconds ?? 0) / 60);
  const accuracy = data?.dashboard?.['avg_recognition_accuracy'];

  return (
    <div>
      <h1>{t('dashboard.welcome', { name: user?.full_name || user?.email || '' })}</h1>
      <ErrorBox error={error} onRetry={reload} />
      {loading && <Loading />}
      <div className="stats-row">
        <StatCard label={t('dashboard.totalSessions')} value={progress?.total_sessions ?? 0} />
        <StatCard label={t('dashboard.avgScore')} value={formatScore(progress?.avg_score)} />
        <StatCard label={t('dashboard.timeSpent')} value={t('dashboard.minutes', { count: minutes })} />
        <StatCard label={t('dashboard.accuracy')} value={typeof accuracy === 'number' ? `${accuracy.toFixed(1)}%` : '—'} />
      </div>
      <ActivityCharts points={progress?.points ?? []} />
      <section>
        <h2>{t('dashboard.recentSessions')}</h2>
        <SessionsTable sessions={data?.dashboard?.recent_sessions ?? []} />
      </section>
    </div>
  );
}

function useAsyncDashboard() {
  return useAsync(loadDashboard, []);
}
